// WebMCP catalog-search tool: pure filter + format of the marketplace catalog
// into a tool result an in-browser agent can read. Pricing stays in USD only -
// the CatalogApi shape is already USD (see CLAUDE.md; never expose credits).
import type { CatalogApi } from '@/features/catalog/model';
import { matchesCatalogCard } from './catalogMatcher';
import { ceilingUsd, quotedRateUsd } from './pricing';

/** A WebMCP / MCP tool result: a list of content blocks. */
export interface WebMcpToolResult {
  content: { type: 'text'; text: string }[];
}

/** JSON Schema for the search_catalog tool input. */
export const SEARCH_CATALOG_SCHEMA = {
  type: 'object',
  properties: {
    query: {
      type: 'string',
      description: 'Keyword matched against complete platform and endpoint data (e.g. "reddit", "ecommerce", "seo").',
    },
    category: {
      type: 'string',
      description: 'Optional category id to filter by (e.g. "social", "shop", "search").',
    },
    limit: {
      type: 'number',
      description: 'Maximum number of results to return (1-50, default 20).',
    },
  },
  additionalProperties: false,
} as const;

export interface SearchCatalogArgs {
  query?: string;
  category?: string;
  limit?: number;
}

const SITE = 'https://getanyapi.com';

/**
 * Filter the catalog by keyword/category and format the matches as a WebMCP
 * text result. Prices are USD in two denominations: `fromPriceUsdPerRequest` is
 * what one request is billed - exact on a flat SKU, the ceiling on a metered
 * one - and `fromPriceUsdPer1k` is the rate per 1,000 of whatever the SKU
 * actually bills for, named by `priceUnit`. The per-1,000 figure is what an
 * agent should quote to a person, and quoting it without `priceUnit` would say
 * "requests" about a SKU that charges per result.
 * AnyAPI is the provider. Pure - given the catalog it returns the same result every time.
 */
export function searchCatalog(apis: CatalogApi[], args: SearchCatalogArgs): WebMcpToolResult {
  const q = (args.query ?? '').trim();
  const cat = (args.category ?? '').trim().toLowerCase();
  const limit = Math.max(1, Math.min(Math.trunc(args.limit ?? 20) || 20, 50));

  const matched = apis
    .filter((a) => {
      if (cat && a.category.toLowerCase() !== cat) return false;
      return matchesCatalogCard(a, q);
    })
    .slice(0, limit);

  const results = matched.map((a) => ({
    id: a.id,
    name: a.name,
    category: a.category,
    description: a.description,
    fromPriceUsdPerRequest: a.price.perRequestUsd,
    fromPriceUsdPer1k: quotedRateUsd(a.price),
    priceUnit: a.price.metered && a.price.unit ? a.price.unit : 'request',
    pricing: a.price.metered ? 'metered' : 'flat',
    ...(a.price.metered ? { pricePerUnitUsd: a.price.perUnitUsd } : {}),
    // An agent relaying a price must see the optional extras, or it quotes the
    // metered ceiling as the most a request can cost and under-quotes anyone
    // who switches one on.
    ...(a.price.addons?.length
      ? {
          optionalExtras: a.price.addons.map((addon) => ({
            inputField: addon.field,
            buys: addon.label,
            addsUsdPerRequest: addon.usd,
          })),
          maxPriceUsdPerRequestWithExtras: ceilingUsd(a.price),
        }
      : {}),
    url: `${SITE}${a.detailHref ?? '/catalog'}`,
  }));

  const text = JSON.stringify(
    { count: results.length, provider: 'AnyAPI', currency: 'USD', results },
    null,
    2,
  );
  return { content: [{ type: 'text', text }] };
}
